import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, User, Calendar, Star, Heart, CheckCircle, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";
import UserAddressMap from "@/components/admin/users/UserAddressMap";
import UserBookingsTab from "@/components/admin/users/UserBookingsTab";
import UserReviewsTab from "@/components/admin/users/UserReviewsTab";
import UserFavoritesTab from "@/components/admin/users/UserFavoritesTab";

interface UserDetailsData {
  id: string;
  email: string;
  display_name: string;
  is_verified: boolean;
  tos_accepted: boolean;
  address?: string;
  location?: string;
  photo_url?: string;
  about_me?: string;
  role: string;
  created_at?: string;
  updated_at?: string;
  last_sign_in_at?: string;
  bookings_count: number;
  reviews_count: number;
  favorites_count: number;
  is_profile_complete: boolean;
}

const UserDetails = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data: userDetails, isLoading, error } = useQuery({
    queryKey: ['user-details', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      // Fetch user profile and role information
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          auth_created_at,
          last_sign_in_at
        `)
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Fetch user role
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();

      // Fetch related counts
      const [bookingsResult, reviewsResult, favoritesResult] = await Promise.all([
        supabase
          .from('bookings')
          .select('id', { count: 'exact' })
          .eq('customer_id', userId),
        supabase
          .from('reviews')
          .select('id', { count: 'exact' })
          .eq('customer_id', userId),
        supabase
          .from('favorites')
          .select('id', { count: 'exact' })
          .eq('user_id', userId)
      ]);

      // Calculate profile completeness
      const isProfileComplete = !!(profile.address && profile.location);

      return {
        ...profile,
        role: userRole?.role || 'user',
        created_at: profile.auth_created_at,
        updated_at: profile.updated_at,
        last_sign_in_at: profile.last_sign_in_at,
        bookings_count: bookingsResult.count || 0,
        reviews_count: reviewsResult.count || 0,
        favorites_count: favoritesResult.count || 0,
        is_profile_complete: isProfileComplete
      } as UserDetailsData;
    },
    enabled: !!userId
  });

  if (isLoading) {
    return (
      <AdminLayout title="User Details">
        <div className="space-y-6">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !userDetails) {
    return (
      <AdminLayout title="User Details">
        <div className="text-center py-8">
          <p className="text-destructive">Failed to load user details</p>
          <Button onClick={() => navigate('/admin/users')} className="mt-4">
            Back to Users
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="User Details">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/users')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                {userDetails.photo_url ? (
                  <img
                    src={userDetails.photo_url}
                    alt={userDetails.display_name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold">{userDetails.display_name}</h2>
                <p className="text-sm text-muted-foreground">{userDetails.email}</p>
              </div>
            </div>
          </div>
          <Badge variant={userDetails.role === 'admin' ? 'default' : userDetails.role === 'vendor' ? 'secondary' : 'outline'}>
            {userDetails.role}
          </Badge>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              {userDetails.is_verified ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div>
                <p className="text-sm font-medium">Email Status</p>
                <p className={`text-xs ${userDetails.is_verified ? 'text-green-600' : 'text-red-600'}`}>
                  {userDetails.is_verified ? 'Verified' : 'Not Verified'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              {userDetails.tos_accepted ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div>
                <p className="text-sm font-medium">Terms of Service</p>
                <p className={`text-xs ${userDetails.tos_accepted ? 'text-green-600' : 'text-red-600'}`}>
                  {userDetails.tos_accepted ? 'Accepted' : 'Not Accepted'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              {userDetails.is_profile_complete ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <div>
                <p className="text-sm font-medium">Profile Status</p>
                <p className={`text-xs ${userDetails.is_profile_complete ? 'text-green-600' : 'text-red-600'}`}>
                  {userDetails.is_profile_complete ? 'Complete' : 'Incomplete'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium">Last Active</p>
                <p className="text-xs text-muted-foreground">
                  {userDetails.last_sign_in_at 
                    ? format(new Date(userDetails.last_sign_in_at), 'MMM dd, yyyy')
                    : 'Never'
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profile Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Display Name</p>
                <p>{userDetails.display_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p>{userDetails.email}</p>
              </div>
              {userDetails.address && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Address</p>
                  <p>{userDetails.address}</p>
                </div>
              )}
              {userDetails.location && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Location</p>
                  <p>{userDetails.location}</p>
                </div>
              )}
              {userDetails.about_me && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">About</p>
                  <p>{userDetails.about_me}</p>
                </div>
              )}
              
              {userDetails.address && (
                <UserAddressMap 
                  address={userDetails.address} 
                  location={userDetails.location} 
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account Created</p>
                <p>{userDetails.created_at ? format(new Date(userDetails.created_at), 'PPp') : 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Profile Updated</p>
                <p>{userDetails.updated_at ? format(new Date(userDetails.updated_at), 'PPp') : 'Never'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Sign In</p>
                <p>{userDetails.last_sign_in_at ? format(new Date(userDetails.last_sign_in_at), 'PPp') : 'Never'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">User Role</p>
                <Badge variant={userDetails.role === 'admin' ? 'default' : userDetails.role === 'vendor' ? 'secondary' : 'outline'}>
                  {userDetails.role}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Tabs */}
        <Card>
          <CardContent className="p-6">
            <Tabs defaultValue="bookings" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="bookings" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Bookings ({userDetails.bookings_count})
                </TabsTrigger>
                <TabsTrigger value="reviews" className="flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Reviews ({userDetails.reviews_count})
                </TabsTrigger>
                <TabsTrigger value="favorites" className="flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  Favorites ({userDetails.favorites_count})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="bookings" className="mt-6">
                <UserBookingsTab userId={userId!} />
              </TabsContent>
              
              <TabsContent value="reviews" className="mt-6">
                <UserReviewsTab userId={userId!} />
              </TabsContent>
              
              <TabsContent value="favorites" className="mt-6">
                <UserFavoritesTab userId={userId!} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default UserDetails;